// useMetricsData.ts
import { useSelector } from 'react-redux';
import {
  selectors,
  LIMIT_METRIC_ALPHA,
  LIMIT_METRIC_BETA,
  LIMIT_METRIC_SHARPE,
  LIMIT_METRIC_TREYNOR,
  LIMIT_METRIC_VAR,
  METRICS_TYPES,
  LIMIT_METRIC_VOLATILITY,
  DURATION_METRICS,
  PORTFOLIO_METRICS,
  HISTORICAL_SCENARIO,
  LIMIT_METRIC_DURATION,
  LIMIT_METRIC_PORTFOLIO,
  LIMIT_METRIC_MDD,
} from 'state';
import { formatters } from 'services';
import { useReportingCurrency } from '../../state/app/utils';

type KeyInfo = {
  name: string;
  limit_index: number;
};

export const metricsNameDict: { [key: string]: KeyInfo } = {
  portfolio_volatility: {
    name: 'Portfolio Volatility',
    limit_index: LIMIT_METRIC_VOLATILITY,
  },
  alpha: { name: 'Alpha', limit_index: LIMIT_METRIC_ALPHA },
  beta: { name: 'Beta', limit_index: LIMIT_METRIC_BETA },
  sharpe_ratio: { name: 'Sharpe Ratio', limit_index: LIMIT_METRIC_SHARPE },
  treynor_ratio: { name: 'Treynor Ratio', limit_index: LIMIT_METRIC_TREYNOR },
  portfolio_duration: {
    name: 'Portfolio Duration',
    limit_index: LIMIT_METRIC_DURATION,
  },
  portfolio_dv01: {
    name: 'Portfolio DV01',
    limit_index: LIMIT_METRIC_PORTFOLIO,
  },
  market_value: { name: 'Market Value', limit_index: 1002 },
  var05: { name: 'VaR', limit_index: LIMIT_METRIC_VAR },
  mdd: { name: 'Maximum Drawdown', limit_index: LIMIT_METRIC_MDD },
};

export type MetricsDataItem = {
  name: string;
  value: string;
  key: number;
  raw_value: number;
};

export const useMetricsData = (): MetricsDataItem[] => {
  const metrics = useSelector(selectors.metrics);
  const portfolioDurationShort = useSelector(selectors.portfolioDurationShort);
  const portfolioDurationLong = useSelector(selectors.portfolioDurationLong);
  const hasHTM = useSelector(selectors.sessionHasHTMBonds);
  const hasAFS = useSelector(selectors.sessionHasAFSBonds);
  const HTMorAFS = hasAFS || hasHTM;

  const { last_market_value, last_portfolio_return, session } = useSelector(
    selectors.player
  );
  const activeSession = useSelector(selectors.activeSession);
  const lastMetrics = { ...(metrics.slice(-1)[0] || {}) };

  // Remove unwanted properties
  delete lastMetrics.timestamp;
  delete lastMetrics.id;

  const reportingCurrency = useReportingCurrency(session);
  const data: MetricsDataItem[] = [];
  if (!HTMorAFS) {
    data.push({
      key: LIMIT_METRIC_VAR,
      name: 'VaR, ' + reportingCurrency.symbol,
      value: formatters.million(lastMetrics.var05),
      raw_value: lastMetrics.var05,
    });
  }
  data.push(
    {
      key: LIMIT_METRIC_SHARPE,
      name: 'Sharpe Ratio',
      value: formatters.metricsCommas2Digits(lastMetrics.sharpe_ratio),
      raw_value: lastMetrics.sharpe_ratio,
    },
    {
      key: LIMIT_METRIC_TREYNOR,
      name: 'Treynor Ratio',
      value: formatters.metricsCommas2Digits(lastMetrics.treynor_ratio),
      raw_value: lastMetrics.treynor_ratio,
    },
    {
      key: LIMIT_METRIC_ALPHA,
      name: 'Alpha',
      value: formatters.metricsCommas4Digits(lastMetrics.alpha),
      raw_value: lastMetrics.alpha,
    },
    {
      key: LIMIT_METRIC_BETA,
      name: 'Beta',
      value: formatters.metricsCommas4Digits(lastMetrics.beta),
      // value: String(lastMetrics.beta),
      raw_value: lastMetrics.beta,
    },
    {
      key: LIMIT_METRIC_VOLATILITY,
      name: 'Volatility',
      value: formatters.metricsPercent1Digit(lastMetrics.portfolio_volatility),
      raw_value: lastMetrics.portfolio_volatility,
    },
    {
      key: LIMIT_METRIC_MDD,
      name: 'Maximum Drawdown',
      value: formatters.metricsPercent1Digit(lastMetrics.mdd),
      raw_value: lastMetrics.mdd,
    }
  );

  if (activeSession.scenario_type !== HISTORICAL_SCENARIO) {
    data.push({
      key: DURATION_METRICS,
      name: 'Duration (long/short)',
      value: `${formatters.commas2Digits(
        portfolioDurationLong
      )} / ${formatters.commas2Digits(portfolioDurationShort)}`,
      raw_value: portfolioDurationShort,
    }); // in the past there DV01 was also added if session is not historical
  }
  data.push({
    key: PORTFOLIO_METRICS,
    name: 'Portfolio',
    value: `${formatters.millionDollars(
      last_market_value
    )} / ${formatters.percent1Digit(last_portfolio_return)}`,
    raw_value: last_portfolio_return,
  });

  // filter out hidden metrics
  // return data.filter(item => activeSession.display_metrics.includes(item.key));
  return data.filter(item => activeSession.metrics_limits[item.key].visible);
};

export const useMetricsKeys = () => {
  const metrics = useSelector(selectors.metrics);
  const { scenario_type, display_metrics } = useSelector(
    selectors.activeSession
  );
  const hasHTM = useSelector(selectors.sessionHasHTMBonds);
  const hasAFS = useSelector(selectors.sessionHasAFSBonds);
  const HTMorAFS = hasAFS || hasHTM;
  const lastMetrics = { ...(metrics.slice(-1)[0] || {}) };
  delete lastMetrics.timestamp;
  delete lastMetrics.id;
  let names = display_metrics.map(
    index => METRICS_TYPES[index as keyof typeof METRICS_TYPES]
  );
  if (HTMorAFS) {
    names = names.filter(name => name !== 'VaR');
  }
  const metricsKeys = Object.keys(lastMetrics).filter(key => {
    // console.log(key);
    if (!metricsNameDict[key]) {
      return;
    }
    return names.includes(metricsNameDict[key].name);
  });
  return metricsKeys;
};
